import { List, User } from "@phosphor-icons/react";
import { useMutation } from "@tanstack/react-query";
import { actions } from "astro:actions";
import Link from "astro-typesafe-routes/link/react";
import { use, useCallback, useState } from "react";

import { AuthForm } from "~/components/AuthForm";
import { Avatar } from "~/components/Avatar";
import { Menu, MenuAction, MenuButton, MenuList } from "~/components/Menu";
import { Modal } from "~/components/Modal";
import { UserContext } from "~/components/UserContext";
import { useLocale } from "~/hooks/locale";
import { getUserDisplayName } from "~/utils/user";

import { ColorModeToggle } from "./ColorModeToggle";

export const UserMenu: React.FC = () => {
  const { t } = useLocale();
  const user = use(UserContext);
  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: actions.auth.logout.orThrow,
    onSuccess: () => {
      window.location.reload();
    },
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  return (
    <>
      <Menu>
        <MenuButton className="btn gap-3 rounded-full border-base-300 bg-base-100 shadow-sm max-sm:btn-circle hover:bg-base-200 sm:pr-2">
          <List className="size-5 max-sm:hidden" />
          {user ? (
            <Avatar user={user} />
          ) : (
            <div className="flex size-8 rounded-full bg-base-300">
              <User weight="bold" className="m-auto size-4" />
            </div>
          )}
        </MenuButton>
        <MenuList>
          <ul className="menu">
            {user ? (
              <>
                <li className="menu-title">{getUserDisplayName(user)}</li>
                <li>
                  <Link to="/account/packs">{t("my_packs")}</Link>
                </li>
                <li>
                  <Link to="/account/assignments">{t("my_assignments")}</Link>
                </li>
                <li>
                  <Link to="/account/bookmarks">{t("my_bookmarks")}</Link>
                </li>
                <li>
                  <Link to="/account/collections">{t("my_collections")}</Link>
                </li>
                <li>
                  <Link to="/account">{t("account_settings")}</Link>
                </li>
                <li>
                  <Link to="/account/billing">{t("billing_settings")}</Link>
                </li>
                <li>
                  <button
                    disabled={isPending || isSuccess}
                    onClick={async () => {
                      mutate();
                    }}
                  >
                    {t("log_out")}
                  </button>
                </li>
              </>
            ) : (
              <>
                <li>
                  <MenuAction onClick={openModal}>
                    <strong>{t("sign_up")}</strong>
                  </MenuAction>
                </li>
                <li>
                  <MenuAction onClick={openModal}>{t("log_in")}</MenuAction>
                </li>
              </>
            )}
            <li>
              <ColorModeToggle />
            </li>
          </ul>
        </MenuList>
      </Menu>
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
      >
        <AuthForm />
      </Modal>
    </>
  );
};
